import { useEffect, useState } from "react";
import { getTour } from "@/lib/toursApi";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface TourViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tourId?: number;
}

const TourViewModal = ({ open, onOpenChange, tourId }: TourViewModalProps) => {
  const { toast } = useToast();
  const [tour, setTour] = useState<any>(null);

  useEffect(() => {
    if (open && tourId) {
      loadTourData();
    }
  }, [open, tourId]);

  const loadTourData = async () => {
    if (!tourId) return;
    
    try {
      const data = await getTour(tourId);
      setTour(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatTourType = (type: string) => {
    const types: Record<string, string> = {
      'driver': 'Driver',
      'guide': 'Guide',
      'staff': 'Staff',
      'safari': 'Safari'
    };
    return types[type] || type;
  };

  const formatPricingType = (type: string) => {
    const types: Record<string, string> = {
      'per_person': 'Per Person',
      'per_family': 'Per Family',
      'per_group': 'Per Group'
    };
    return types[type] || type;
  };

  if (!tour) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Tour Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {tour.image_url && (
            <div className="w-full">
              <img 
                src={tour.image_url} 
                alt={tour.title}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Badge variant="secondary">{formatTourType(tour.type)}</Badge>
            <Badge variant="outline">{formatPricingType(tour.pricing_type)}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tour Code</Label>
              <Input value={tour.tour_code || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Duration (hours)</Label>
              <Input value={tour.duration_hours?.toString() || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={tour.title || ""} disabled />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={tour.description || ""} disabled rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Max Capacity</Label>
              <Input value={tour.max_capacity?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Supplier Name</Label>
              <Input value={tour.supplier_name || ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost Supplier</Label>
              <Input value={tour.cost_supplier?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Tax Percentage</Label>
              <Input value={tour.tax_percentage?.toString() || ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Price OP</Label>
              <Input value={tour.price_op?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price AG</Label>
              <Input value={tour.price_ag?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price CF</Label>
              <Input value={tour.price_cf?.toString() || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-base font-semibold">Extra Hour Pricing</Label>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Extra Hour OP</Label>
                <Input value={tour.price_extra_hour_op?.toString() || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>Extra Hour AG</Label>
                <Input value={tour.price_extra_hour_ag?.toString() || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>Extra Hour CF</Label>
                <Input value={tour.price_extra_hour_cf?.toString() || ""} disabled />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cost Supplier Extra Hour</Label>
            <Input value={tour.cost_supplier_extra_hour?.toString() || ""} disabled />
          </div>

          {tour.hcte_notes && (
            <div className="space-y-2">
              <Label>HCTE Notes</Label>
              <Textarea value={tour.hcte_notes} disabled rows={3} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TourViewModal;
